// Generated file : do not modify
export enum AccountEntryDirections
{
  NONE = 0,
  DEBIT,
  CREDIT
};

export enum AccountEntryKinds
{
  NONE = 0,
  INVOICE,
  CREDIT_NOTE,
  PAYMENT,
  REFUND,
  OPENING_BALANCE,
  MANUAL_ADJUSTMENT,
  ACCRUED_RECEIVABLE
};

export enum AccountEntryStatuts
{
  NONE = 0,
  PENDING,
  POSTED,
  REVERSED,
  CANCELLED
};

export enum AccountEntryCreditModes
{
  NONE = 0,
  REFUNDABLE,
  REUSABLE_ONLY
};

import Owner from '@/extend/classes/Owner';
import { ListFilter } from '@/extend/classes/ListData';
import ListData from '@/extend/classes/ListData';
import { ObjTypes } from '@/generated/classes/ObjTypes';
import { NotifDefTypes } from '@/generated/classes/NotifDef';
import { storeSession } from '@/generated/stores';
import { type AxiosResponse } from 'axios';
import {getRequestConfig} from "@/framework/base/helpers/HttpHelper.ts";

// Écritures de compte
export class AccountStatementQuery {
  // Établissement
  public etablissementId: number;
  // Du
  public dateFrom: Date;
  // Au
  public dateTo: Date;

  constructor() {
    this.etablissementId = 0;
    this.dateFrom = new Date(1901,1,1,0,0,0,0);
    this.dateTo = new Date(1901,1,1,0,0,0,0);
  }
}

export default class AccountEntry  {
  // Identifiant unique
  public id: number;
  // Titulaire
  public owner: Owner;
  // Sens
  public direction: AccountEntryDirections;
  // Montant
  public amount: number;
  // Nature
  public kind: AccountEntryKinds;
  // Statut
  public statut: AccountEntryStatuts;
  // Utilisation du crédit
  public creditMode: AccountEntryCreditModes;
  // Type de la source
  public sourceType: ObjTypes;
  // Identifiant de la source
  public sourceId: number;
  // Facture associée
  public billId: number;
  // Type de mouvement du livre
  public ledgerCode: string;
  // Composante de l'écriture
  public componentCode: string;
  // Pièce comptable
  public documentReference: string;
  // Mode de règlement
  public paymentMethod: string;
  // Référence
  public reference: string;
  // Libellé
  public label: string;
  // Date d'effet
  public effectiveAt: Date;
  // Écriture de contrepassation
  public reversalEntryId: number;
  // Montant affecté
  public allocatedAmount: number;
  // Montant restant
  public openAmount: number;
  // GUID de création du store
  public createId: string;
  // Commande non exécutée
  public failed: boolean;
  // Donnée pouvant être  modifiée
  public canUpdate: boolean;
  // Donnée pouvant être supprimée
  public canDelete: boolean;
  // Display des objets
  public display: string;
  // Type de notification
  public notifDefType: NotifDefTypes;
  // Type
  public ownerType: number;
  // Identifiant
  public ownerId: number;
  // Nom
  public ownerLabel: string;

  constructor() {
    this.id = 0;
    this.owner = new Owner();
    this.direction = AccountEntryDirections.NONE;
    this.amount = 0;
    this.kind = AccountEntryKinds.NONE;
    this.statut = AccountEntryStatuts.NONE;
    this.creditMode = AccountEntryCreditModes.NONE;
    this.sourceType = ObjTypes.NONE;
    this.sourceId = 0;
    this.billId = 0;
    this.ledgerCode = '';
    this.componentCode = '';
    this.documentReference = '';
    this.paymentMethod = '';
    this.reference = '';
    this.label = '';
    this.effectiveAt = new Date(1901,1,1,0,0,0,0);
    this.reversalEntryId = 0;
    this.allocatedAmount = 0;
    this.openAmount = 0;
    this.createId = '';
    this.failed = false;
    this.canUpdate = true;
    this.canDelete = true;
    this.display = '';
    this.notifDefType = NotifDefTypes.NONE;
    this.ownerType = 0;
    this.ownerId = 0;
    this.ownerLabel = '';
  }

  public static fromJson (obj: AccountEntry): AccountEntry {
    const newAccountEntry: AccountEntry = new AccountEntry();
    newAccountEntry.id = obj.id;
    newAccountEntry.owner = JSON.parse(JSON.stringify(obj.owner));
    newAccountEntry.direction = obj.direction;
    newAccountEntry.amount = obj.amount;
    newAccountEntry.kind = obj.kind;
    newAccountEntry.statut = obj.statut;
    newAccountEntry.creditMode = obj.creditMode;
    newAccountEntry.sourceType = obj.sourceType;
    newAccountEntry.sourceId = obj.sourceId;
    newAccountEntry.billId = obj.billId;
    newAccountEntry.ledgerCode = obj.ledgerCode;
    newAccountEntry.componentCode = obj.componentCode;
    newAccountEntry.documentReference = obj.documentReference;
    newAccountEntry.paymentMethod = obj.paymentMethod;
    newAccountEntry.reference = obj.reference;
    newAccountEntry.label = obj.label;
    newAccountEntry.effectiveAt = new Date(obj.effectiveAt);
    newAccountEntry.reversalEntryId = obj.reversalEntryId;
    newAccountEntry.allocatedAmount = obj.allocatedAmount;
    newAccountEntry.openAmount = obj.openAmount;
    newAccountEntry.createId = obj.createId;
    newAccountEntry.failed = obj.failed;
    newAccountEntry.canUpdate = obj.canUpdate;
    newAccountEntry.canDelete = obj.canDelete;
    newAccountEntry.display = obj.display;
    newAccountEntry.notifDefType = obj.notifDefType;
    newAccountEntry.ownerType = obj.ownerType;
    newAccountEntry.ownerId = obj.ownerId;
    newAccountEntry.ownerLabel = obj.ownerLabel;
    return newAccountEntry;
  }

  public toJsonAccountEntry (): string {
    const json = {
      owner: this.owner,
      direction: this.direction,
      amount: this.amount,
      kind: this.kind,
      statut: this.statut,
      creditMode: this.creditMode,
      sourceType: this.sourceType,
      sourceId: this.sourceId,
      billId: this.billId,
      ledgerCode: this.ledgerCode,
      componentCode: this.componentCode,
      documentReference: this.documentReference,
      paymentMethod: this.paymentMethod,
      reference: this.reference,
      label: this.label,
      effectiveAt: this.effectiveAt ? new Date (this.effectiveAt).toString() : this.effectiveAt,
      reversalEntryId: this.reversalEntryId,
      allocatedAmount: this.allocatedAmount,
      openAmount: this.openAmount,
      canUpdate: this.canUpdate,
      canDelete: this.canDelete,
      notifDefType: this.notifDefType,
    }
    return JSON.stringify(json);
  }

  public fromAccountEntry (obj: AccountEntry): void {
    this.id = obj.id;
    this.owner = obj.owner;
    this.direction = obj.direction;
    this.amount = obj.amount;
    this.kind = obj.kind;
    this.statut = obj.statut;
    this.creditMode = obj.creditMode;
    this.sourceType = obj.sourceType;
    this.sourceId = obj.sourceId;
    this.billId = obj.billId;
    this.ledgerCode = obj.ledgerCode;
    this.componentCode = obj.componentCode;
    this.documentReference = obj.documentReference;
    this.paymentMethod = obj.paymentMethod;
    this.reference = obj.reference;
    this.label = obj.label;
    this.effectiveAt = new Date(obj.effectiveAt);
    this.reversalEntryId = obj.reversalEntryId;
    this.allocatedAmount = obj.allocatedAmount;
    this.openAmount = obj.openAmount;
    this.createId = obj.createId;
    this.failed = obj.failed;
    this.canUpdate = obj.canUpdate;
    this.canDelete = obj.canDelete;
    this.display = obj.display;
    this.notifDefType = obj.notifDefType;
    this.ownerType = obj.ownerType;
    this.ownerId = obj.ownerId;
    this.ownerLabel = obj.ownerLabel;
  }

  public compareAccountEntry (value: AccountEntry): boolean {
    return (this.toJsonAccountEntry() == value.toJsonAccountEntry());
  }

  public cloneAccountEntry (): AccountEntry {
    return AccountEntry.fromJson(this);
  }

  public createForStoreAccountEntry (): Promise<AccountEntry> {
    return new Promise((resolve, reject) => {
      if (storeSession.httpCx != undefined) {
        return storeSession.httpCx.post('api/accountentries/', this, getRequestConfig())
          .then((res: AxiosResponse) => {
            if (res.status == 200) {
              this.fromAccountEntry(res.data);
              resolve(this);
            } else {
              reject(res.data);
            }
          }).catch(() => {
            reject(null);
          });
      }
    });
  }

  public static list (filter: ListFilter): Promise<ListData> {
    return new Promise((resolve, reject) => {
      if (storeSession.httpCx != undefined) {
        return storeSession.httpCx.post('api/accountentries/list', filter, getRequestConfig())
          .then((res: AxiosResponse) => {
            if (res.status == 200) {
              resolve(res.data);
            } else {
              reject(res.data);
            }
          }).catch(() => {
            reject(null);
          });
      }
    });
  }

  public static hasMovements (query: AccountStatementQuery): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (storeSession.httpCx != undefined) {
        return storeSession.httpCx.post('api/accountentries/hasMovements', query, getRequestConfig())
          .then((res: AxiosResponse) => {
            if (res.status == 200) {
              resolve(res.data);
            } else {
              reject(res.data);
            }
          }).catch(() => {
            reject(null);
          });
      }
    });
  }

  public static reverse (accountEntry: AccountEntry): Promise<AccountEntry> {
    return new Promise((resolve, reject) => {
      if (storeSession.httpCx != undefined) {
        return storeSession.httpCx.post('api/accountentries/reverse', accountEntry, getRequestConfig())
          .then((res: AxiosResponse) => {
            if (res.status == 200) {
              resolve(AccountEntry.fromJson(res.data));
            } else {
              reject(res.data);
            }
          }).catch(() => {
            reject(null);
          });
      }
    });
  }

  public static getForStore (id: number): Promise<ListData> {
    return new Promise((resolve, reject) => {
      if (storeSession.httpCx != undefined) {
        return storeSession.httpCx.get('api/AccountEntries/' + id.toString(), getRequestConfig())
          .then((res: AxiosResponse) => {
            if (res.status == 200) {
              const listData = res.data as ListData;
              listData.reccords[0] = AccountEntry.fromJson(listData.reccords[0] as AccountEntry);
              resolve(listData);
            } else {
              reject(res.data);
            }
          }).catch(() => {
            reject(null);
          });
      }
    });
  }

  public static get (id: number): Promise<AccountEntry> {
    return new Promise((resolve, reject) => {
      if (storeSession.httpCx != undefined) {
        return storeSession.httpCx.get('api/AccountEntries/' + id.toString(), getRequestConfig())
          .then((res: AxiosResponse) => {
            if (res.status == 200) {
              const listData = res.data as ListData;
              const data = AccountEntry.fromJson(listData.reccords[0] as AccountEntry);
              resolve(data);
            } else {
              reject(res.data);
            }
          }).catch(() => {
            reject(null);
          });
      }
    });
  }

  public static getDate(accountEntrys: AccountEntry[]) {
    accountEntrys.forEach((item) => {
      item.effectiveAt = item.effectiveAt ? new Date(item.effectiveAt) : item.effectiveAt;
    })
  }
}
